import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import dayjs from 'dayjs';

import i18n from 'config/i18n';
import { getLocale } from 'utils/date';

import {
  ClientResponse,
  EmployeeResponse,
  EventResponse,
  ResourceResponse
} from 'generated/index';

import NamesCell from './components/NamesCell/NamesCell';
import RecurrenceCell from './components/RecurrenceCell/RecurrenceCell';

const t = i18n.getFixedT(getLocale());

export type AppointmentsOverviewRow = EventResponse;

export const getAppointmentRowId = (row: AppointmentsOverviewRow): string =>
  `${row.id}-${row.date}`;

/**
 * Whether an appointment belongs to a series, and a bulk action therefore has to ask whether it
 * applies to this occurrence only or to this one and all subsequent ones.
 *
 * Not `isRecurring`: that means "carries a recurrence rule of its own", which a series exception
 * does not - it is a detached child of its series, so it would slip past the question and be
 * treated as a standalone appointment. `appointmentType` expresses belonging to a series directly,
 * for instances and exceptions alike. (`isRecurringException` would work too, but it is misnamed:
 * the backend fills it from `isPartOfSeries`.)
 *
 * The two types are listed rather than excluding SINGLE_OCCURRENCE, so this stays the exact mirror
 * of `AppointmentPresentationModel.isPartOfSeries()`: DEFAULT and a missing type are not a series
 * either.
 */
export const isPartOfSeries = (appointment: EventResponse): boolean =>
  appointment.appointmentType === 'SERIES_INSTANCE' ||
  appointment.appointmentType === 'SERIES_EXCEPTION';

export const containsAppointmentInSeries = (
  appointments: EventResponse[]
): boolean => appointments.some(isPartOfSeries);

// TODO: This is a placeholder until a decision is made on the correct name to be displayed
export const getEmployeeLabel = (employee: EmployeeResponse): string =>
  employee.nameInformal ||
  employee.nameCompound ||
  [employee.lastName, employee.firstName].filter(Boolean).join(', ') ||
  '';

const getClientLabel = (client: ClientResponse): string =>
  client.displayName ||
  client.fullName ||
  client.nameWithInitials ||
  [client.lastName, client.firstName].filter(Boolean).join(', ') ||
  '';

const getResourceLabel = (resource: ResourceResponse): string =>
  resource.name || '';

export const useColumnDefinitions =
  (): GridColDef<AppointmentsOverviewRow>[] => [
    {
      field: 'appointmentDate',
      headerName: t('appointmentsOverview.columns.appointmentDate'),
      minWidth: 130,
      sortComparator: (a: dayjs.Dayjs, b: dayjs.Dayjs) => a.diff(b),
      valueGetter: (_, row) => dayjs(row.start),
      valueFormatter: (value: dayjs.Dayjs) => value.format('DD-MM-YYYY')
    },
    {
      field: 'appointmentTime',
      headerName: t('appointmentsOverview.columns.appointmentTime'),
      minWidth: 120,
      sortable: false,
      valueGetter: (_, row) =>
        `${dayjs(row.start).format('HH:mm')} - ${dayjs(row.end).format(
          'HH:mm'
        )}`
    },
    {
      field: 'activity',
      headerName: t('appointmentsOverview.columns.activity'),
      minWidth: 200,
      valueGetter: (_, row) => {
        const name = row.activity?.name || '';
        const code = row.activity?.code;
        return code ? `${name} (${code})` : name;
      }
    },
    {
      field: 'recurrence',
      headerName: t('appointmentsOverview.columns.recurrence'),
      minWidth: 180,
      sortable: false,
      renderCell: ({ row }) =>
        row.isRecurring || row.isRecurringException ? (
          <RecurrenceCell row={row} />
        ) : null
    },
    {
      field: 'employees',
      headerName: t('appointmentsOverview.columns.employees'),
      minWidth: 200,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <NamesCell names={(row.employees || []).map(getEmployeeLabel)} />
      )
    },
    {
      field: 'clients',
      headerName: t('appointmentsOverview.columns.clients'),
      minWidth: 200,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <NamesCell names={(row.clients || []).map(getClientLabel)} />
      )
    },
    {
      field: 'resources',
      headerName: t('appointmentsOverview.columns.resources'),
      minWidth: 160,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <NamesCell names={(row.resources || []).map(getResourceLabel)} />
      )
    }
  ];
